import { Component, OnDestroy, inject } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AxiosError } from 'axios';
import {
  PartnersDataTableItem,
  PartnerService,
} from 'src/app/services/partner/partner.service';
import { PayeePopupComponent } from '../../../../shared/payee-popup/payee-popup.component';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { NgTemplateOutlet } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { PayeeCopyButtonComponent } from '../../../../shared/payee-buttons/payee-copy-button/payee-copy-button.component';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeDropzoneComponent } from '../../../../shared/payee-dropzone/payee-dropzone.component';
import { PayeeMiniIconButtonComponent } from '../../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';

type LogoKind = 'formLogo' | 'smallLogo' | 'workflowNotificationLogo';

@Component({
  selector: 'app-edit-partner-dialog',
  templateUrl: './edit-partner-dialog.component.html',
  styleUrls: ['./edit-partner-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    NgTemplateOutlet,
    MatCheckbox,
    PayeeCopyButtonComponent,
    PayeeButtonComponent,
    MatDialogClose,
    PayeeDropzoneComponent,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
  ],
})
export class EditPartnerDialogComponent implements OnDestroy {
  private fb = inject(FormBuilder);
  private partnerService = inject(PartnerService);
  private snackbar = inject(MatSnackBar);
  private dialogRef =
    inject<MatDialogRef<EditPartnerDialogComponent>>(MatDialogRef);
  private loading = false;

  readonly data = inject<PartnersDataTableItem>(MAT_DIALOG_DATA);

  readonly formGroup = this.fb.group({
    name: this.fb.control(this.data.name, {
      validators: Validators.required,
      nonNullable: true,
    }),
    change_partner_token: this.fb.control(false, { nonNullable: true }),
  });

  readonly logoAccept =
    'image/png,image/jpeg,image/gif,image/webp,image/svg+xml,.png,.jpg,.jpeg,.gif,.webp,.svg';

  partnerToken?: string;
  formLogo: File | null = null;
  smallLogo: File | null = null;
  workflowNotificationLogo: File | null = null;
  formLogoPreview: string | null = this.data.form_logo_url;
  smallLogoPreview: string | null = this.data.small_logo_url;
  workflowNotificationLogoPreview: string | null =
    this.data.workflow_notification_logo_url;

  get name() {
    return this.formGroup.controls.name;
  }
  get change_partner_token() {
    return this.formGroup.controls.change_partner_token;
  }

  ngOnDestroy(): void {
    for (const preview of [
      this.formLogoPreview,
      this.smallLogoPreview,
      this.workflowNotificationLogoPreview,
    ]) {
      if (preview?.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    }
  }

  filesDropped(kind: LogoKind, files: FileList): void {
    this.setLogo(kind, files[0] ?? null);
  }

  removeLogo(kind: LogoKind): void {
    this.setLogo(kind, null);
  }

  private setLogo(kind: LogoKind, file: File | null): void {
    const previewKey = `${kind}Preview` as
      | 'formLogoPreview'
      | 'smallLogoPreview'
      | 'workflowNotificationLogoPreview';
    const fallbackKey = (
      {
        formLogo: 'form_logo_url',
        smallLogo: 'small_logo_url',
        workflowNotificationLogo: 'workflow_notification_logo_url',
      } as const
    )[kind];

    const previousUrl = this[previewKey];
    if (previousUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(previousUrl);
    }

    this[kind] = file;
    this[previewKey] = file ? URL.createObjectURL(file) : this.data[fallbackKey];
  }

  async submit() {
    if (this.formGroup.invalid || this.loading) {
      this.name.markAsDirty();

      return;
    }

    const formValue = this.formGroup.getRawValue();

    try {
      this.loading = true;
      const { partner } = await this.partnerService.editPartner(
        this.data.id,
        {
          ...formValue,
          form_logo: this.formLogo,
          small_logo: this.smallLogo,
          workflow_notification_logo: this.workflowNotificationLogo,
        },
      );
      if (!formValue.change_partner_token) {
        this.dialogRef.close(true);
        return;
      }

      this.partnerToken = partner.api_key;
    } catch (error) {
      this.name.markAsDirty();

      if (error instanceof AxiosError) {
        if (error.response?.status === 422) {
          const errors: undefined | Record<string, string[]> =
            error.response?.data?.errors;
          if (errors) {
            Object.entries(errors).forEach(([key, errors]) => {
              const control = this.formGroup.get(key);
              if (control) {
                control.setErrors({
                  unknown: errors[0],
                });
              } else {
                this.formGroup.setErrors({
                  unknown: errors[0],
                });
              }
            });
            return;
          }
        }
      }
      this.snackbar.open('Ismeretlen hiba!', 'OK', { duration: 5000 });
      console.error('Unknown error while creating partner!', error);
    } finally {
      this.loading = false;
    }
  }
}
